import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Chip,
  TablePagination,
} from "@mui/material";
import axiosInstance from "../../../../../../services/axiosInstance";
import { useParams } from "react-router-dom";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";

// Utility to format date
const formatDateTime = (date) => {
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Utility for INDIAN currency formatting
const formatCurrency = (amount) =>
  amount.toLocaleString("en-AE", {
    style: "currency",
    currency: "AED",
  });

const StageHistoryTable = () => {
  const { id: dealId } = useParams();
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);

  // pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const getStageHistories = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getstageHistories}/${dealId}`
      );
      setHistories(res.data.histories || []);
      setPage(0); // reset to first page when data changes
    } catch (err) {
      console.error("Error fetching stage histories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStageHistories();
  }, [dealId]);

  // slice data for current page
  const paginatedHistories = histories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", pt: 0 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#4b5563" }}
        >
          Stage History
        </Typography>

        {histories.length > 0 && !loading && (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            {histories.length} change{histories.length > 1 ? "s" : ""} recorded
          </Typography>
        )}
      </Box>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {/* No Data */}
      {!loading && histories.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px dashed #e5e7eb",
            bgcolor: "#f9fafb",
            p: 2.5,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", fontStyle: "italic" }}
          >
            No data available.
          </Typography>
        </Paper>
      )}

      {/* Table + Pagination */}
      {!loading && histories.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid #ebebeb",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "#fafafa",
                    "& th": {
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#374151",
                      py: 1.25,
                    },
                  }}
                >
                  <TableCell>Stage</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Stage Duration (In Days)</TableCell>
                  <TableCell>Modified Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedHistories.map((item, index) => {
                  const globalIndex = page * rowsPerPage + index;

                  // First global entry = current stage
                  if (globalIndex === 0) {
                    return (
                      <TableRow
                        key={item._id}
                        sx={{
                          "&:hover": { bgcolor: "#f9fafb" },
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          {item.stageName}{" "}
                          <Chip
                            label="Current"
                            size="small"
                            sx={{
                              ml: 1,
                              fontSize: 10,
                              height: 18,
                              bgcolor: "#e0f2fe",
                              color: "#0369a1",
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#16a34a",
                            fontSize: 13,
                          }}
                        >
                          {formatCurrency(item.amount)}
                        </TableCell>

                        <TableCell sx={{ fontSize: 13 }}>
                          <strong style={{ color: "#2563eb" }}>
                            Current Stage
                          </strong>
                        </TableCell>

                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              src={item.userAvatar || ""}
                              sx={{ width: 26, height: 26, fontSize: 13 }}
                            />
                            <Typography
                              sx={{ fontSize: 12.5, color: "#374151" }}
                            >
                              {formatDateTime(item.changeTime)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // For previous stages: compute duration using global indexes
                  const currentDate = new Date(item.changeTime);
                  const prevDate = new Date(
                    histories[globalIndex - 1].changeTime
                  );
                  const diffMs = prevDate - currentDate;
                  const diffDays = Math.max(
                    1,
                    Math.ceil(diffMs / (1000 * 60 * 60 * 24))
                  );

                  return (
                    <TableRow
                      key={item._id}
                      sx={{
                        bgcolor: globalIndex % 2 === 0 ? "#ffffff" : "#f9fafb",
                        "&:hover": { bgcolor: "#f3f4f6" },
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, fontSize: 13 }}>
                        {item.stageName}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#16a34a",
                          fontSize: 13,
                        }}
                      >
                        {formatCurrency(item.amount)}
                      </TableCell>

                      <TableCell
                        sx={{ fontWeight: 600, color: "#374151", fontSize: 13 }}
                      >
                        {diffDays}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={item.userAvatar || ""}
                            sx={{ width: 26, height: 26, fontSize: 13 }}
                          />
                          <Typography sx={{ fontSize: 12.5, color: "#374151" }}>
                            {formatDateTime(item.changeTime)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={histories.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page:"
          />
        </Paper>
      )}
    </Box>
  );
};

export default StageHistoryTable;
